import type { ComponentType, SvelteComponent } from 'svelte';
import DeviceConditionNode from './DeviceConditionNode.svelte';
import TimeConditionNode from './TimeConditionNode.svelte';
import ManualConditionNode from './ManualConditionNode.svelte';
import WeatherConditionNode from './WeatherConditionNode.svelte';
import SunriseSunsetConditionNode from './SunriseSunsetConditionNode.svelte';
import EnergyConditionNode from './EnergyConditionNode.svelte';
import SceneConditionNode from './SceneConditionNode.svelte';
import ScheduleConditionNode from './ScheduleConditionNode.svelte';
import VariableConditionNode from './VariableConditionNode.svelte';
import MathConditionNode from './MathConditionNode.svelte';

export interface ConditionNodeRegistryEntry {
	nodeType: string;
	component: ComponentType<SvelteComponent>;
	category: 'condition';
	defaultLabel: string;
	icon: string;
	color: string;
	defaultData: Record<string, unknown>;
}

export const conditionNodeRegistry: ConditionNodeRegistryEntry[] = [
	{
		nodeType: 'condition',
		component: DeviceConditionNode as ComponentType<SvelteComponent>,
		category: 'condition',
		defaultLabel: 'Condition',
		icon: 'fa-question-circle',
		color: '#3b82f6',
		defaultData: { label: 'Condition', triggerType: 'device' },
	},
	{
		nodeType: 'timeCondition',
		component: TimeConditionNode as ComponentType<SvelteComponent>,
		category: 'condition',
		defaultLabel: 'Time Condition',
		icon: 'fa-clock',
		color: '#6366f1',
		defaultData: {
			label: 'Time Condition',
			timeSubType: 'daily',
			startTime: '08:00',
			endTime: '22:00',
		},
	},
	{
		nodeType: 'manualCondition',
		component: ManualConditionNode as ComponentType<SvelteComponent>,
		category: 'condition',
		defaultLabel: 'Manual Condition',
		icon: 'fa-hand-pointer',
		color: '#f59e0b',
		defaultData: { label: 'Manual Condition' },
	},
	{
		nodeType: 'weatherCondition',
		component: WeatherConditionNode as ComponentType<SvelteComponent>,
		category: 'condition',
		defaultLabel: 'Weather Condition',
		icon: 'fa-cloud-sun',
		color: '#6366f1',
		defaultData: { label: 'Weather Condition', weatherCond: 'temperature' },
	},
	{
		nodeType: 'sunriseSunsetCondition',
		component: SunriseSunsetConditionNode as ComponentType<SvelteComponent>,
		category: 'condition',
		defaultLabel: 'Sunrise/Sunset',
		icon: 'fa-sun',
		color: '#f97316',
		defaultData: { label: 'Sunrise/Sunset', sunType: 'sunrise' },
	},
	{
		nodeType: 'energyCondition',
		component: EnergyConditionNode as ComponentType<SvelteComponent>,
		category: 'condition',
		defaultLabel: 'Energy Condition',
		icon: 'fa-bolt',
		color: '#10b981',
		defaultData: { label: 'Energy Exchange Price' },
	},
	{
		nodeType: 'sceneCondition',
		component: SceneConditionNode as ComponentType<SvelteComponent>,
		category: 'condition',
		defaultLabel: 'Scene Condition',
		icon: 'fa-layer-group',
		color: '#3b82f6',
		defaultData: { label: 'Scene Condition' },
	},
	{
		nodeType: 'alarmCondition',
		component: SceneConditionNode as ComponentType<SvelteComponent>,
		category: 'condition',
		defaultLabel: 'Alarm Condition',
		icon: 'fa-bell',
		color: '#f59e0b',
		defaultData: { label: 'Alarm Condition', filterType: 'alarm' },
	},
	{
		nodeType: 'scheduleCondition',
		component: ScheduleConditionNode as ComponentType<SvelteComponent>,
		category: 'condition',
		defaultLabel: 'Schedule',
		icon: 'fa-calendar',
		color: '#6366f1',
		defaultData: { label: 'Schedule', activeTimeIntervals: [] },
	},
	{
		nodeType: 'mathCondition',
		component: MathConditionNode as ComponentType<SvelteComponent>,
		category: 'condition',
		defaultLabel: 'Math Expression',
		icon: 'fa-square-root-variable',
		color: '#8b5cf6',
		defaultData: { label: 'Math Expression', expression: '', params: [], mathScript: {} },
	},
	{
		nodeType: 'variableCondition',
		component: VariableConditionNode as ComponentType<SvelteComponent>,
		category: 'condition',
		defaultLabel: 'Variable Check',
		icon: 'fa-code',
		color: '#f97316',
		defaultData: { label: 'Variable Check', variableName: '', operator: 'eq', variableValue: '' },
	},
];

/** Build a nodeTypes map compatible with SvelteFlow from the registry */
export function buildConditionNodeTypes(): Record<string, ComponentType<SvelteComponent>> {
	return Object.fromEntries(conditionNodeRegistry.map((e) => [e.nodeType, e.component]));
}
